/**
 * Inspection rounds — a whole round filed in one request, and read back.
 *
 * Shapes follow `POST /v1/inspections` with the round inline and
 * `GET /v1/inspections/rounds` (backend work/rounds.py). A round is an
 * inspection of a pack checklist: every item answered pass, find or skip, the
 * findings noted, and a work order raised for each finding at the priority the
 * inspector gave it. The platform stamps who walked it and when.
 */

/** The airports pack's movement area round: the airfield inspection board's checklist. */
export const FIELD_ROUND = "oema_field_round";

/** One answer, as the round sends it. */
export interface RoundAnswerInput {
  itemKey: string;
  /** one of the item's options — for the field round: pass, find or skip */
  value: string;
  notes?: string;
  /** required with a skip: why it was not looked at */
  skippedReason?: string;
  /** minted by the client, so a resend lands on the same answer */
  clientId: string;
  /** what the board recorded that the checklist has no field for */
  attrs?: Record<string, unknown>;
  /** where the finding is (WGS 84); its work order is put there */
  lon?: number;
  lat?: number;
}

/** A work order wanted for one finding. */
export interface RoundWorkInput {
  /** the client id of the answer (a finding) it is for */
  observation: string;
  title: string;
  description?: string;
  /** 1 (most urgent) to 5 */
  priority: number;
  slaHours?: number;
  area?: string;
  trade?: string;
}

export interface InspectionRoundInput {
  siteId: string;
  template: string;
  clientId: string;
  attrs?: Record<string, unknown>;
  answers: RoundAnswerInput[];
  complete: boolean;
  /** close the round although findings lack the photograph the pack asks for — the record says so */
  allowMissingEvidence: boolean;
  work: RoundWorkInput[];
}

/** A work order a round raised. */
export interface RoundWork {
  id: string;
  number: string;
  status: string;
  priority?: number;
  /** the platform's id of the answer it came from */
  observationId?: string;
  /** the client id of that answer, when it had one */
  observation?: string;
  itemKey?: string;
}

export interface InspectionRoundResult {
  id: string;
  reference: string;
  status: string;
  created: boolean;
  /** true when the platform already had this round and answered from what it holds */
  replayed: boolean;
  completedAt?: string;
  score?: number;
  answers: number;
  failed: number;
  skipped: number;
  /** items closed without the photograph the pack asks for */
  missingEvidence: string[];
  warnings: string[];
  workOrders: RoundWork[];
}

/** One answer, as the platform holds it. */
export interface RoundAnswer {
  id: string;
  clientId?: string;
  itemKey: string;
  /** pass | fail | na (skipped) | value */
  result?: string;
  valueText?: string;
  notes?: string;
  skippedReason?: string;
  attrs: Record<string, unknown>;
  defectId?: string;
  photos: number;
}

export interface InspectionRound {
  id: string;
  reference: string;
  clientId?: string;
  template: string;
  templateName: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  score?: number;
  /** the platform's name for the person who walked it */
  inspector?: string;
  capturedOffline?: boolean;
  attrs: Record<string, unknown>;
  answers: RoundAnswer[];
  workOrders: RoundWork[];
}

export interface InspectionRoundQuery {
  siteId: string;
  templates: readonly string[];
  limit?: number;
}

/** The short reference the platform gives an inspection: the first eight characters of its id. */
export const roundReference = (id: string): string => id.slice(0, 8).toUpperCase();
